import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaRocket, 
  FaUsers, 
  FaLightbulb,  
  FaCode, 
  FaFileUpload,
  FaCheckCircle,
  FaExclamationCircle,
  FaPaperPlane,
  FaRegSmileBeam,
  FaBalanceScale,
  FaUserCheck
} from 'react-icons/fa';
import './QuemSomos.css';

const QuemSomos = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    area: '',
    portfolio: '',
    message: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      resume: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simular envio
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({ name: '', email: '', area: '', portfolio: '', message: '', resume: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 5000);
    }
  };

  const timelineData = [
    {
      year: '2023',
      title: 'Vencedores do concurso de ideias no INOVAR',
      description: 'O começo da jornada comecou no evento INOVAR, onde vencemos o concurso de ideias com o projeto "MicroCodie"',
      icon: <FaRocket />
    },
    {
      year: '2024-1',
      title: 'Pré-Incubação no TecnoPARQ - UFV',
      description: 'Foram 6 meses de preparação para a incubação, onde tivemos a oportunidade de aprimorar nossos conhecimentos, produtos e serviços.',
      icon: <FaCode />
    },
    {
      year: '2024-2',
      title: 'Abertura da empresa',
      description: 'Onde o CNPJ se consolidou e começamos a trabalhar em nossos primeiros projetos',
      icon: <FaUsers />
    },
    {
      year: '2025',
      title: 'Incubação no TecnoPARQ - UFV',
      description: 'Após pivotarmos, entramos na incubação, obtendo mais suporte para o crescimento dentro do TecnoPARQ.',
      icon: <FaLightbulb />
    },
    {
      year: '2025-2',
      title: 'Inovação e Futuro',
      description: 'Estamos sempre olhando para frente, buscando novas soluções e tecnologias para transformar o amanhã.',
      icon: <FaRocket />
    }
  ];

  // const teamMembers = [
  //   {
  //     name: 'João Silva',
  //     role: 'CEO & Desenvolvedor Full-Stack',
  //     photo: 'https://via.placeholder.com/200x200/00AFEF/FFFFFF?text=JS',
  //     linkedin: 'https://linkedin.com/in/joaosilva'
  //   },
  //   {
  //     name: 'Maria Santos',
  //     role: 'UX/UI Designer',
  //     photo: 'https://via.placeholder.com/200x200/0081A5/FFFFFF?text=MS',
  //     linkedin: 'https://linkedin.com/in/mariasantos'
  //   },
  //   {
  //     name: 'Pedro Costa',
  //     role: 'Desenvolvedor Backend',
  //     photo: 'https://via.placeholder.com/200x200/606062/FFFFFF?text=PC',
  //     linkedin: 'https://linkedin.com/in/pedrocosta'
  //   },
  //   {
  //     name: 'Pedro Costa',
  //     role: 'Desenvolvedor Backend',
  //     photo: 'https://via.placeholder.com/200x200/606062/FFFFFF?text=PC',
  //     linkedin: 'https://linkedin.com/in/pedrocosta'
  //   },
  // ];

  const values = [
    {
      title: 'Integridade',
      description: 'Agimos com honestidade, ética e transparência em todas as nossas relações.',
      icon: <FaBalanceScale />
    },
    {
      title: 'Inovação',
      description: 'Buscamos constantemente novas ideias, tecnologias e soluções para entregar inovação real aos nossos clientes.',
      icon: <FaLightbulb />
    },
    {
      title: 'Qualidade',
      description: 'Buscamos excelência em cada detalhe, entregando soluções confiáveis e de alto padrão.',
      icon: <FaCheckCircle />
    },
    {
      title: 'Foco no cliente',
      description: 'Colocamos o cliente no centro de tudo, ouvindo suas necessidades e entregando soluções personalizadas que superam expectativas.',
      icon: <FaUserCheck />
    }
  ];

  const TimelineItem = ({ item, index }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const isMobile = window.innerWidth <= 768;

    return (
      <motion.div
        ref={ref}
        className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
        initial={isMobile ? { opacity: 0, y: 50 } : { opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
        animate={isInView ? (isMobile ? { opacity: 1, y: 0 } : { opacity: 1, x: 0 }) : (isMobile ? { opacity: 0, y: 50 } : { opacity: 0, x: index % 2 === 0 ? -50 : 50 })}
        transition={{ duration: 0.6, delay: index * 0.2 }}
      >
        <div className="timeline-content">
          <div className="timeline-header">
            <span className="timeline-icon">{item.icon}</span>
            <span className="timeline-year">{item.year}</span>
          </div>
          <h3>{item.title}</h3>
          <p>{item.description}</p>
        </div>
      </motion.div>
    );
  };

  return (
    <section id="quem-somos" className="section quem-somos-section">
      <div className="container">
        {/* 1. Introdução */}
        <motion.div
          className="intro-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Sobre Nós</h2>
            <p className="section-subtitle">Transformando ideias em realidade digital</p>
          </div>

          <div className="intro-content">
            <div className="intro-text">
            <p>
              A <strong>MicroCodie</strong> nasceu dentro do <a href="https://centev.ufv.br/" target="_blank" rel="noopener noreferrer" style={{textDecorationLine: 'none'}}><strong>TecnoPARQ</strong></a>, 
              incubadora de empresas de base tecnológica em Viçosa-MG. Desde o início, 
              surgimos com o propósito de fazer diferente: desenvolver soluções escaláveis, 
              flexíveis, fáceis de usar e com tempo de construção reduzido.
            </p>
            <p>
              Acreditamos que tecnologia não precisa ser complicada, pelo contrário, 
              ela deve ser uma ponte entre ideias e realidade. Queremos ser acessíveis. 
              Nosso compromisso é com os empreendedores que precisam tirar suas ideias do 
              papel de forma descomplicada e eficiente.
            </p>
            <p>
              Nossas soluções não nascem de suposições. São fruto de dores reais, 
              trazidas por pessoas da área que vivem os desafios no dia a dia. 
              Por isso, cada produto que desenvolvemos é pensado para resolver problemas 
              concretos, com foco na experiência do usuário e em gerar valor prático.
            </p>
            </div>
            
            <div className="intro-highlight">
              <div className="highlight-box">
                <FaRocket className="highlight-icon" />
                <h3>"Inovação é o nosso DNA"</h3>
                <p>Transformamos desafios em oportunidades através da tecnologia</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 2. Nossa Trajetória */}
        <motion.div
          className="timeline-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Nossa Trajetória</h2>
            <p className="section-subtitle">Uma jornada de crescimento e inovação</p>
          </div>

          <div className="timeline-container">
            <div className="timeline-line"></div>
            {timelineData.map((item, index) => (
              <TimelineItem key={index} item={item} index={index} />
            ))}
          </div>
        </motion.div>

        {/* 3. Nossa Cultura */}
        <motion.div
          className="culture-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Nossa Cultura</h2>
            <p className="section-subtitle">O que nos move e como trabalhamos</p>
          </div>

          <div className="culture-content">
            <div className="culture-text">
              <p>
                Na MicroCodie, acreditamos que o sucesso de uma empresa está diretamente ligado à 
                qualidade de sua cultura organizacional. Criamos um ambiente onde a criatividade, 
                a colaboração e a inovação florescem naturalmente.
              </p>
              <p>
                Nossa equipe trabalha com autonomia e responsabilidade, sempre buscando a excelência 
                em cada projeto. Valorizamos a diversidade de pensamento e incentivamos o aprendizado 
                contínuo, pois sabemos que a tecnologia evolui rapidamente e precisamos estar sempre 
                atualizados.
              </p>
            </div>

            <div className="culture-features">
              <div className="culture-feature">
                <FaUsers className="feature-icon" />
                <h4>Trabalho em Equipe</h4>
                <p>Colaboração e sinergia para resultados extraordinários</p>
              </div>
              <div className="culture-feature">
                <FaLightbulb className="feature-icon" />
                <h4>Inovação Constante</h4>
                <p>Busca incessante por novas soluções e tecnologias</p>
              </div>
              <div className="culture-feature">
                <FaRegSmileBeam className="feature-icon" />
                <h4>Atendimento Humanizado</h4>
                <p>Colocamos as pessoas no centro, ouvindo e entendendo as necessidades de cada cliente.</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 4. Nossa Equipe */}
        {/* <motion.div
          className="team-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Nossa Equipe</h2>
            <p className="section-subtitle">Profissionais apaixonados por tecnologia</p>
          </div>

          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                className="team-member"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10 }}
              >
                <div className="member-photo">
                  <img src={member.photo} alt={member.name} loading="lazy" />
                </div>
                <div className="member-info">
                  <h3>{member.name}</h3>
                  <p className="member-role">{member.role}</p>
                  <a 
                    href={member.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="member-linkedin"
                    aria-label={`LinkedIn de ${member.name}`}
                  >
                    <FaLinkedin />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div> */}

        {/* 5. Banco de Talentos */}
        <motion.div
          className="careers-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Quer trabalhar com a gente?</h2>
            <p className="section-subtitle">Junte-se ao nosso time de inovadores</p>
          </div>

          <div className="careers-content">
            <div className="careers-text">
              <p>
                Estamos sempre em busca de talentos que queiram fazer 
                parte de nossa jornada de inovação. Se você é desenvolvedor, designer ou profissional 
                de TI e busca um ambiente desafiador e colaborativo, queremos conhecer você!
              </p>
              <p>
                Oferecemos oportunidades de crescimento, aprendizado contínuo e projetos desafiadores 
                que impactam a vida de pessoas. Venha fazer parte do nosso time!
              </p>
            </div>

            <div className="careers-form-container">
              <form onSubmit={handleSubmit} className="careers-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Nome completo *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu nome completo"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="emai  l">E-mail *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Digite seu e-mail"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="resume">Currículo *</label>
                  <div className="file-upload">
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="resume"
                      name="resume"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      required
                    />
                    <label htmlFor="resume" className="file-label">
                      <FaFileUpload />
                      <span>{formData.resume ? formData.resume.name : 'Escolher arquivo'}</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Mensagem (opcional)</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Conte-nos um pouco sobre você e suas expectativas"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : (
                    <>
                      Enviar currículo
                      <FaPaperPlane />
                    </>
                  )}
                </button>
              </form>

              {submitStatus && (
                <motion.div
                  className={`submit-feedback ${submitStatus}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  {submitStatus === 'success' ? (
                    <>
                      <FaCheckCircle />
                      <span>Currículo enviado com sucesso! Entraremos em contato em breve.</span>
                    </>
                  ) : (
                    <>
                      <FaExclamationCircle />
                      <span>Erro ao enviar currículo. Tente novamente.</span>
                    </>
                  )}
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* 6. Missão, Visão e Valores */}
        <motion.div
          className="values-section"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="section-header">
            <h2 className="section-title">Missão, Visão e Valores</h2>
            <p className="section-subtitle">Os pilares que norteiam nossa empresa</p>
          </div>

          <div className="values-content">
            <div className="mission-vision">
              <div className="mission-card">
                <FaRocket className="card-icon" />
                <h3>Nossa Missão</h3>
                <p>
                  Transformar a forma como as empresas operam por meio de soluções de 
                  software e consultoria especializada, promovendo eficiência, 
                  crescimento e impacto positivo.
                </p>
              </div>

              <div className="vision-card">
                <FaLightbulb className="card-icon" />
                <h3>Nossa Visão</h3>
                <p>
                  Ser referência no desenvolvimento de soluções tecnológicas para o setor 
                  de tecnologia, promovendo sustentabilidade e eficiência.
                </p>
              </div>
            </div>

            <div className="values-grid">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  className="value-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <div className="value-icon">{value.icon}</div>
                  <h4>{value.title}</h4>
                  <p>{value.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default QuemSomos; 